import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'

import styles from './DisbandTeamDialog.module.scss'

import { isApiError } from '@/api'
import type { EnterpriseMemberRowResponse, TeamResponse } from '@/api/generated'
import { moveMemberToTeam } from '@/api/members'
import { disbandTeam } from '@/api/team'
import { getTeamMembersTable } from '@/api/teamMembersTable'
import ConfirmDialog from '@/components/ConfirmDialog'
import DataTable from '@/components/DataTable'
import FormErrorBanner from '@/components/FormErrorBanner'
import { enterpriseTeamsQueryKey } from '@/hooks/useEnterpriseTeams'
import Select from '@/components/Select'
import { useToast } from '@/components/Toast/ToastContext'
import {
  MY_ACTIVE_COST_CENTER_QUERY_KEY,
  pickDefaultDestinationTeamId,
  TEAM_MEMBERS_PAGE_SIZE,
  TEAM_MEMBERS_TABLE_QUERY_KEY,
} from '@/pages/app/Team/Department/utils'
import { fullName } from '@/pages/app/UsageV2/TeamMembersTab/TeamMembersTable'

interface DisbandTeamDialogProps {
  isOpen: boolean
  team: TeamResponse | null
  allTeams: TeamResponse[]
  enterpriseName: string
  enterpriseId: string
  onClose: () => void
}

const DisbandTeamDialog = ({
  isOpen,
  team,
  allTeams,
  enterpriseName,
  enterpriseId,
  onClose,
}: DisbandTeamDialogProps) => {
  const queryClient = useQueryClient()
  const { updateToast } = useToast()

  const [destinations, setDestinations] = useState<Record<string, string>>({})
  const [formError, setFormError] = useState<string | null>(null)

  const { data: membersData, isLoading: isLoadingMembers } = useQuery({
    queryKey: [TEAM_MEMBERS_TABLE_QUERY_KEY, team?.id ?? '', { sortBy: 'email', sortDir: 'asc' }],
    queryFn: ({ signal }) =>
      getTeamMembersTable({
        teamId: team!.id,
        limit: TEAM_MEMBERS_PAGE_SIZE,
        sortBy: 'email',
        sortDir: 'asc',
        signal,
      }).then((r) => r.data),
    enabled: isOpen && !!team,
  })

  const members: EnterpriseMemberRowResponse[] = useMemo(() => membersData?.rows ?? [], [membersData])

  const destinationOptions = useMemo(() => {
    if (!team) return []
    return allTeams.filter((t) => t.id !== team.id).map((t) => ({ value: t.id, label: t.name }))
  }, [allTeams, team])

  // Default each member's destination to the General team in the same cost center.
  useEffect(() => {
    if (!isOpen || !team) return
    const defaultId = pickDefaultDestinationTeamId(allTeams, team)
    if (!defaultId) return
    const next: Record<string, string> = {}
    for (const m of members) next[m.userId] = defaultId
    setDestinations(next)
    setFormError(null)
  }, [isOpen, team, members, allTeams])

  const mutation = useMutation({
    mutationFn: async () => {
      if (!team) throw new Error('No team selected')

      // Pre-validate before firing any requests so a missing destination doesn't
      // leave the team half-migrated.
      for (const m of members) {
        if (!destinations[m.userId]) throw new Error('Pick a destination for every member')
      }

      // Reassign every member in parallel. If any move fails we stop short of
      // disbanding so the user can retry without leaving the team gone.
      const movesToFire = members.filter((m) => destinations[m.userId] !== team.id)
      const results = await Promise.allSettled(
        movesToFire.map((m) => moveMemberToTeam(destinations[m.userId], { user_id: m.userId }))
      )
      const failures = results.filter((r): r is PromiseRejectedResult => r.status === 'rejected')
      if (failures.length > 0) {
        const first = failures[0].reason
        const detail = isApiError(first) ? (first.detail ?? first.message) : 'Some member moves failed.'
        throw {
          message: detail,
          status: isApiError(first) ? first.status : 500,
          detail:
            failures.length === 1
              ? detail
              : `${failures.length} of ${movesToFire.length} member moves failed. ${detail}`,
        }
      }

      await disbandTeam(team.id)
    },
    onSuccess: () => {
      updateToast('Department deleted', 'success')
      queryClient.invalidateQueries({ queryKey: enterpriseTeamsQueryKey(enterpriseId) })
      queryClient.invalidateQueries({ queryKey: [TEAM_MEMBERS_TABLE_QUERY_KEY] })
      // Disbanding the caller's own team would move them to another team's
      // General; even when the disbanded team isn't the caller's, every
      // member of the CC may have shifted — keep the active-cost-center
      // snapshot in lockstep so the next Create-Department open evaluates
      // sole-admin against the post-disband roster.
      queryClient.invalidateQueries({ queryKey: [MY_ACTIVE_COST_CENTER_QUERY_KEY] })
      onClose()
    },
    onError: (err: unknown) => {
      setFormError(
        isApiError(err)
          ? (err.detail ?? 'Failed to delete department. Please try again.')
          : 'Failed to delete department. Please try again.'
      )
    },
  })

  const memberColumns = useMemo<ColumnDef<EnterpriseMemberRowResponse, unknown>[]>(
    () => [
      {
        id: 'member',
        header: 'Member',
        cell: ({ row }) => {
          const m = row.original
          const hasName = !!(m.firstName || m.lastName)
          return (
            <div className={styles.memberCell}>
              <span className={styles.memberName}>{fullName(m) ?? '—'}</span>
              {m.email && hasName && <span className={styles.memberEmail}>{m.email}</span>}
            </div>
          )
        },
      },
      {
        id: 'destination',
        header: 'Move to',
        cell: ({ row }) => {
          const m = row.original
          return (
            <Select
              value={destinations[m.userId] ?? ''}
              options={destinationOptions}
              onChange={(val) => setDestinations((prev) => ({ ...prev, [m.userId]: val }))}
              placeholder="Pick a department"
              isDisabled={mutation.isPending}
            />
          )
        },
      },
    ],
    [destinations, destinationOptions, mutation.isPending]
  )

  if (!team) return null

  const ready = members.every((m) => Boolean(destinations[m.userId]))

  return (
    <ConfirmDialog
      isOpen={isOpen}
      title={`Delete ${team.name}?`}
      minWidth={520}
      confirmLabel={mutation.isPending ? 'Deleting…' : 'Delete department'}
      confirmType="destructive"
      isConfirming={mutation.isPending}
      isConfirmDisabled={!ready || isLoadingMembers}
      onConfirm={() => mutation.mutate()}
      onCancel={() => {
        if (mutation.isPending) return
        onClose()
      }}
      message={
        <div className={styles.body}>
          <p className={styles.helper}>
            If someone is removed from this department, this action does not remove anyone from your {enterpriseName}{' '}
            bill.
          </p>

          <div className={styles.tableWrapper}>
            <DataTable
              data={members}
              columns={memberColumns}
              isLoading={isLoadingMembers}
              loadingMessage="Loading members…"
              emptyMessage="No members to reassign. This department will be deleted."
              ariaLabel={`Reassign members of ${team.name}`}
            />
          </div>

          {formError && <FormErrorBanner message={formError} />}
        </div>
      }
    />
  )
}

export default DisbandTeamDialog
